import { MenuItemUpdateData } from '@provider-portal/common/api/types';
import { push } from 'connected-react-router';
import queryString from 'query-string';
import { combineEpics } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, filter, switchMap, withLatestFrom } from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';

import { ApiError } from '@bolteu/react-api-gateway/es';

import { selectSelectedProvider } from 'rdx/account';
import { GlobalAppActions } from 'rdx/actions';
import { AppServices } from 'rdx/middleware';
import { selectQuery } from 'rdx/router';
import { GlobalAppState } from 'rdx/state';

import { getMenuAsyncAction, updateMenuAsyncAction } from './actions';
import { normalizeData } from './scheme';
import { selectMenuDishesEntities, selectMenuOptionsEntities } from './selectors';
import { mapFormValuesToMenuUpdateItems } from './utils';

const getMenuEpic = (
  action$: Observable<GlobalAppActions>,
  state$: Observable<GlobalAppState>,
  { apiClient, bugsnag }: AppServices,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(getMenuAsyncAction.request)),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const { locale } = action.payload;
      const selectedProvider = selectSelectedProvider(state);

      if (!selectedProvider || !selectedProvider.providerId) {
        return of(getMenuAsyncAction.failure(new Error('Missed selected provider id')));
      }

      return from(
        apiClient.portal.getMenu({
          language: locale,
          provider_id: selectedProvider.providerId,
        }),
      ).pipe(
        switchMap((res) => {
          const menu = normalizeData(res);
          return of(
            push({ search: `?selectedCategoryId=${menu?.result?.categories?.[0]}` }),
            getMenuAsyncAction.success(menu),
          );
        }),
        catchError((e) => {
          if (!ApiError.isApiError(e)) {
            bugsnag.notify(e);
          }
          return of(getMenuAsyncAction.failure(e));
        }),
      );
    }),
  );
};

const updateMenuEpic = (
  action$: Observable<GlobalAppActions>,
  state$: Observable<GlobalAppState>,
  { apiClient, bugsnag }: AppServices,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(updateMenuAsyncAction.request)),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const { locale } = action.payload;
      const selectedProvider = selectSelectedProvider(state);
      const dishes = selectMenuDishesEntities(state);
      const options = selectMenuOptionsEntities(state);
      const currentMenuItems = { ...dishes, ...options };

      const menuItems: MenuItemUpdateData[] = mapFormValuesToMenuUpdateItems(
        action.payload.items,
        currentMenuItems,
      );

      if (!menuItems.length || !selectedProvider) {
        return of(updateMenuAsyncAction.failure(new Error('Missed items or selected provider')));
      }

      return from(
        apiClient.portal.updateMenu({
          provider_id: selectedProvider.providerId,
          menu_items: menuItems,
          language: locale,
        }),
      ).pipe(
        switchMap(() => {
          const query = selectQuery(state);

          return of(
            updateMenuAsyncAction.success(),
            getMenuAsyncAction.request({ locale }),
            push({
              search: queryString.stringify({ ...query, mode: 'view' }),
            }),
          );
        }),
        catchError((e) => {
          if (!ApiError.isApiError(e)) {
            bugsnag.notify(e);
          }
          return of(updateMenuAsyncAction.failure(e));
        }),
      );
    }),
  );
};

export const menuEpic = combineEpics(getMenuEpic, updateMenuEpic);
